import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";

// Proove-parity build, Part 3 — minimal, standalone landing pages for
// Stripe's success_url/cancel_url (create_checkout_session in the
// backend). Confirmed real gap (downstream-mismatch-checker finding,
// pre-integration): these routes didn't exist at all, so a patient who
// actually paid via PanelCheckoutScreen's "Pay Now" button got silently
// bounced to the login page with zero confirmation either way. Kept
// deliberately simple and NOT behind ProtectedRoute — the patient isn't
// necessarily logged in yet at this point (this can be the very first
// payment they make, right after registration), and the real, detailed
// per-item payment status/receipt view is MyPayments' own job (Part 4),
// not this page's.

const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

export function PaymentSuccess() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const itemKey = searchParams.get("item_key");

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f4f7ff",
      display: "flex", alignItems: "center", justifyContent: "center", px: 3 }}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}>
        <Box sx={{
          width: { xs: "100%", sm: 480 }, maxWidth: 480, borderRadius: 3, overflow: "hidden",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
          boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
          bgcolor: isDark ? "#111827" : "#ffffff",
        }}>
          <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />
          <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center" }}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}>
              <CheckCircleOutline sx={{ fontSize: 56, color: "#22c55e", mb: 1.5 }} />
            </motion.div>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.gold, mb: 0.75 }}>
              PAYMENT RECEIVED
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
              Thank You
            </Typography>
          </Box>
          <Box sx={{ px: 4, py: 3.5 }}>
            <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, mb: 3, textAlign: "center" }}>
              Your payment{itemKey ? "" : " has been received"}. A confirmation email is on its way.
              Sign in to see your receipt and account details anytime.
            </Typography>
            <Button fullWidth variant="contained" onClick={() => navigate("/")}
              sx={{
                background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
                borderRadius: "10px", py: 1.3, fontWeight: 700, textTransform: "none", fontSize: 15,
                "&:hover": { bgcolor: BRAND.navyDark },
              }}>
              Go to Sign In
            </Button>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}

export function PaymentCancelled() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f4f7ff",
      display: "flex", alignItems: "center", justifyContent: "center", px: 3 }}>
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}>
        <Box sx={{
          width: { xs: "100%", sm: 480 }, maxWidth: 480, borderRadius: 3, overflow: "hidden",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.1)"}`,
          boxShadow: isDark ? "0 24px 80px rgba(0,0,0,0.5)" : "0 24px 80px rgba(0,53,122,0.12)",
          bgcolor: isDark ? "#111827" : "#ffffff",
        }}>
          <Box sx={{ height: 4, background: `linear-gradient(to right, ${BRAND.navy}, ${BRAND.gold})` }} />
          <Box sx={{ bgcolor: BRAND.navy, px: 4, pt: 3.5, pb: 3.5, textAlign: "center" }}>
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 260, damping: 18 }}>
              <ErrorOutline sx={{ fontSize: 56, color: BRAND.gold, mb: 1.5 }} />
            </motion.div>
            <Typography sx={{ fontSize: 14.5, fontWeight: 700, letterSpacing: "0.15em", color: BRAND.gold, mb: 0.75 }}>
              CHECKOUT CANCELLED
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
              No Payment Was Made
            </Typography>
          </Box>
          <Box sx={{ px: 4, py: 3.5 }}>
            <Typography sx={{ fontSize: 16, color: isDark ? "#cbd5e1" : "#334155", lineHeight: 1.8, mb: 3, textAlign: "center" }}>
              Your checkout was cancelled and nothing was charged. Your account is still active —
              sign in to try again anytime from My Payments.
            </Typography>
            <Button fullWidth variant="contained" onClick={() => navigate("/")}
              sx={{
                background: `linear-gradient(135deg, ${BRAND.navy} 0%, #004299 100%)`,
                borderRadius: "10px", py: 1.3, fontWeight: 700, textTransform: "none", fontSize: 15,
                "&:hover": { bgcolor: BRAND.navyDark },
              }}>
              Go to Sign In
            </Button>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
